// The demo plan's "About" drawer, beside the conversation. Desktop only.
//
// This used to hold the plan's objective, roster and decisions; those are pages now
// (`FamilyPage.tsx`, `DecisionsPage.tsx`, chosen from `PageNav.tsx`). What remains is the
// one drawer a demo plan adds: the scenario's own words and the seat's character brief
// (`CharacterBrief.tsx`). A plan that is not a demo renders nothing here.
//
// It is a collapsible flex sibling of the conversation column, hidden by CSS below the
// desktop breakpoint.

import { useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { DemoSeatIntro } from '../demo/useDemoSeatIntro.js';
import { welcomeParagraphs } from '../demo/welcome-text.js';
import { CharacterBrief } from './CharacterBrief.js';

export type DecisionsPanelProps = {
  client: ApiClient;
  planId: string;
  /** Whether this plan is a demo — the drawer exists only on one. */
  isDemo: boolean;
  /** The seat this demo was started from, resolved by `useDemoSeatIntro` — `null` when
   *  there is nothing to name it with. */
  demoSeat: DemoSeatIntro | null;
};

/* Points right, toward the edge the panel collapses into — not left, which reads as
 * opening or going back rather than dismissing. */
function ChevronIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="1rem" height="1rem" aria-hidden="true">
      <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function DecisionsPanel({
  client,
  planId,
  isDemo,
  demoSeat,
}: DecisionsPanelProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!isDemo) {
    return null;
  }
  const aboutLabel = 'Demo';

  if (!open) {
    return (
      <aside className="decisionsPanel decisionsPanel--collapsed" aria-label={aboutLabel}>
        <button
          type="button"
          className="decisionsPanel__reopen"
          onClick={() => setOpen(true)}
        >
          {aboutLabel}
        </button>
      </aside>
    );
  }

  return (
    <aside className="decisionsPanel" aria-label={aboutLabel}>
      <div className="decisionsPanel__head">
        <button
          type="button"
          className="iconButton decisionsPanel__collapse"
          aria-label="Hide character brief"
          onClick={() => setOpen(false)}
        >
          <ChevronIcon />
        </button>
      </div>
      <div className="characterDrawer">
        {demoSeat === null
          ? null
          : welcomeParagraphs(demoSeat).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
        {demoSeat === null ? null : (
          <h2 className="characterDrawer__title">About {demoSeat.firstName}</h2>
        )}
        <CharacterBrief
          client={client}
          planId={planId}
          className="characterDrawer__body"
        />
      </div>
    </aside>
  );
}
